import React, { useEffect, useState, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, ReferenceDot,
} from 'recharts';
import { apiRequest } from '../config/api';
import {
  GPS_ALTITUDE_KEY,
  getChannelConfig,
  transformChannelValue,
} from '../utils/sensorUtils';

// ── Types ──────────────────────────────────────────────────────────────────────

interface Coordinate {
  latitude: number;
  longitude: number;
  altitude: number;
  timestamp: string;
}

interface FlightDiagramsProps {
  flightId: string;
  coordinates?: Coordinate[];
  dateStart: string;
  dateEnd?: string;
  hoverTimestamp?: number | null;
  onHoverTimestamp?: (timestamp: number | null) => void;
  onClickTimestamp?: (timestamp: number) => void;
}

function getLabel(ch: string): string {
  if (ch === GPS_ALTITUDE_KEY) return 'Höhe (GNSS)';
  return getChannelConfig(ch).label;
}
function getUnit(ch: string): string { return getChannelConfig(ch).unit; }
function getColor(ch: string): string { return getChannelConfig(ch).color; }

function formatTime(ts: number | string): string {
  return new Date(ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function buildUnifiedData(
  activeTabs: Set<string>,
  dateStart: string,
  dateEnd: string | undefined,
  coordinates: Coordinate[] = [],
  sensorData: Record<string, Array<{ time: string; value: number }>>
) {
  const startMs = new Date(dateStart).getTime();
  let endMs = startMs;
  if (dateEnd) {
    endMs = new Date(dateEnd).getTime();
  } else {
    let maxTime = startMs;
    if (coordinates && coordinates.length > 0) {
      maxTime = Math.max(maxTime, new Date(coordinates[coordinates.length - 1].timestamp).getTime());
    }
    activeTabs.forEach(tab => {
      const readings = sensorData[tab];
      if (readings && readings.length > 0) {
        maxTime = Math.max(maxTime, new Date(readings[readings.length - 1].time).getTime());
      }
    });
    endMs = maxTime;
  }

  if (endMs <= startMs) return [];

  const steps = 600;
  const intervalMs = (endMs - startMs) / steps;
  const gapThreshold = Math.max(30 * 1000, intervalMs * 3);

  const rawData: Record<string, Array<{ time: number; value: number }>> = {};
  const indices: Record<string, number> = {};
  
  if (activeTabs.has(GPS_ALTITUDE_KEY)) {
    rawData[GPS_ALTITUDE_KEY] = coordinates.map(c => ({
      time: new Date(c.timestamp).getTime(),
      value: parseFloat(c.altitude.toFixed(1))
    })).sort((a, b) => a.time - b.time);
    indices[GPS_ALTITUDE_KEY] = 0;
  }
  activeTabs.forEach(tab => {
    if (tab !== GPS_ALTITUDE_KEY) {
      rawData[tab] = (sensorData[tab] || []).map(r => {
        const transformed = transformChannelValue(tab, r.value) ?? r.value;
        return {
          time: new Date(r.time).getTime(),
          value: parseFloat(transformed.toFixed(3))
        };
      }).sort((a, b) => a.time - b.time);
      indices[tab] = 0;
    }
  });

  const result = [];
  for (let i = 0; i <= steps; i++) {
    const targetTime = startMs + i * intervalMs;
    const row: any = { time: targetTime };
    
    activeTabs.forEach(tab => {
      const arr = rawData[tab];
      if (!arr || arr.length === 0) {
        row[tab] = null;
        return;
      }
      
      let idx = indices[tab];
      while (idx < arr.length - 1 && arr[idx + 1].time <= targetTime) {
        idx++;
      }
      indices[tab] = idx;
      
      const p1 = arr[idx];
      const p2 = idx + 1 < arr.length ? arr[idx + 1] : null;
      let best = p1;
      if (p2 && Math.abs(p2.time - targetTime) < Math.abs(p1.time - targetTime)) {
        best = p2;
      }
      
      if (Math.abs(best.time - targetTime) <= gapThreshold) {
        row[tab] = best.value;
      } else {
        row[tab] = null;
      }
    });
    result.push(row);
  }
  return result;
}

// ── Shared chart style constants ──────────────────────────────────────────────

const TICK_STYLE: React.CSSProperties = { fontSize: 11, fill: '#9aa0ad', fontFamily: 'inherit' };
const Y_LABEL_STYLE: React.CSSProperties = { fontSize: 10, fill: '#9aa0ad', fontFamily: 'inherit' };
const GRID_PROPS = { stroke: '#edf0f5', strokeDasharray: '3 3' };
const CHART_MARGIN = { top: 10, right: 28, left: 4, bottom: 4 };

// ── Custom tooltip ────────────────────────────────────────────────────────────

interface TooltipProps { active?: boolean; payload?: any[]; label?: string; }

const ChartTooltip: React.FC<TooltipProps> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const displayLabel = typeof label === 'number' ? formatTime(label) : label;
  return (
    <div style={{
      background: 'white',
      border: '1px solid #e8eaf0',
      borderRadius: '6px',
      padding: '8px 12px',
      fontSize: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
      fontFamily: 'inherit',
      pointerEvents: 'none',
    }}>
      <p style={{ color: '#687088', margin: '0 0 4px', fontWeight: 600 }}>{displayLabel}</p>
      {payload.map((p: any) => p.value !== null && p.value !== undefined ? (
        <p key={p.dataKey} style={{ color: p.color, margin: '2px 0' }}>
          {p.name}:{' '}
          <strong>{typeof p.value === 'number' ? p.value.toFixed(2) : p.value}</strong>
          {` ${getUnit(p.dataKey)}`}
        </p>
      ) : null)}
      <div style={{ marginTop: '5px', fontSize: '10px', color: '#9aa0ad', borderTop: '1px solid #f1f5f9', paddingTop: '4px' }}>
        Klicken, um in Tabelle anzuzeigen
      </div>
    </div>
  );
};

// ── Empty state ───────────────────────────────────────────────────────────────

const NoData: React.FC<{ message?: string }> = ({ message = 'Keine Daten verfügbar' }) => (
  <div style={{
    height: 260, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    color: '#c5cdd8', gap: '6px',
    borderTop: '1px solid #f0f2f7', marginTop: '8px',
  }}>
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/>
    </svg>
    <p style={{ margin: 0, fontSize: '13px' }}>{message}</p>
  </div>
);

// ── Single-series line chart ──────────────────────────────────────────────────

interface MultiSeriesChartProps {
  data: any[];
  seriesInfo: Array<{
    id: string;
    name: string;
    unit: string;
    color: string;
  }>;
  xDomain: [number, number];
  hoverTimestamp?: number | null;
  onHoverTimestamp?: (timestamp: number | null) => void;
  onClickTimestamp?: (timestamp: number) => void;
}

function getYAxisDomain(channelId: string): [any, any] | undefined {
  switch (channelId) {
    case 'pressure':
      return [(dataMin: number) => Math.min(dataMin, 600), 'auto'];
    default:
      return undefined;
  }
}

const MultiSeriesChart: React.FC<MultiSeriesChartProps> = ({
  data,
  seriesInfo,
  xDomain,
  hoverTimestamp,
  onHoverTimestamp,
  onClickTimestamp,
}) => {
  const [isInternalHover, setIsInternalHover] = useState(false);
  const lastHoverTimeRef = React.useRef<number | null>(null);
  const pendingTimestampRef = React.useRef<number | null>(null);
  const rafIdRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  const hoveredRow = useMemo(() => {
    if (hoverTimestamp == null || !data || data.length === 0) return null;
    let closest = data[0];
    let minDiff = Math.abs(closest.time - hoverTimestamp);
    for (let i = 1; i < data.length; i++) {
      const diff = Math.abs(data[i].time - hoverTimestamp);
      if (diff < minDiff) {
        minDiff = diff;
        closest = data[i];
      } else {
        break;
      }
    }
    return closest;
  }, [data, hoverTimestamp]);

  if (data.length === 0 || seriesInfo.length === 0) return <NoData />;

  const handleMouseMove = (state: any) => {
    setIsInternalHover(true);
    if (!onHoverTimestamp) return;
    if (state && state.isTooltipActive) {
      let ts: number | null = null;
      if (typeof state.activeLabel === 'number') {
        ts = state.activeLabel;
      } else if (typeof state.activeTooltipIndex === 'number' && data[state.activeTooltipIndex]) {
        ts = data[state.activeTooltipIndex].time;
      } else if (state.activePayload && state.activePayload.length > 0) {
        const pTime = state.activePayload[0]?.payload?.time;
        if (typeof pTime === 'number') ts = pTime;
      } else if (state.activeLabel) {
        const parsed = Number(state.activeLabel);
        if (!isNaN(parsed)) ts = parsed;
      }

      if (ts !== null && ts !== pendingTimestampRef.current) {
        pendingTimestampRef.current = ts;
        if (rafIdRef.current === null) {
          rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = null;
            const targetTs = pendingTimestampRef.current;
            if (targetTs !== null && targetTs !== lastHoverTimeRef.current) {
              lastHoverTimeRef.current = targetTs;
              onHoverTimestamp(targetTs);
            }
          });
        }
      }
    }
  };

  const handleMouseLeave = () => {
    setIsInternalHover(false);
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    pendingTimestampRef.current = null;
    if (lastHoverTimeRef.current !== null) {
      lastHoverTimeRef.current = null;
      onHoverTimestamp?.(null);
    }
  };

  const handleClick = (state: any) => {
    if (!onClickTimestamp) return;
    let ts: number | null = null;
    if (state) {
      if (typeof state.activeLabel === 'number') {
        ts = state.activeLabel;
      } else if (typeof state.activeTooltipIndex === 'number' && data[state.activeTooltipIndex]) {
        ts = data[state.activeTooltipIndex].time;
      } else if (state.activePayload && state.activePayload.length > 0) {
        const pTime = state.activePayload[0]?.payload?.time;
        if (typeof pTime === 'number') ts = pTime;
      } else if (state.activeLabel) {
        const parsed = Number(state.activeLabel);
        if (!isNaN(parsed)) ts = parsed;
      }
    }
    if (ts === null) {
      ts = lastHoverTimeRef.current ?? pendingTimestampRef.current;
    }
    if (ts !== null) {
      onClickTimestamp(ts);
    }
  };

  return (
    <ResponsiveContainer width="100%" height={360}>
      <LineChart
        data={data}
        margin={CHART_MARGIN}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <CartesianGrid {...GRID_PROPS} />
        <XAxis
          dataKey="time"
          type="number"
          domain={xDomain}
          tickFormatter={(val) => formatTime(val)}
          tick={TICK_STYLE as any}
          interval="preserveStartEnd"
          minTickGap={70}
          allowDuplicatedCategory={false}
        />
        {seriesInfo.map((s, i) => (
          <YAxis
            key={s.id}
            yAxisId={s.id}
            orientation={i % 2 === 0 ? 'left' : 'right'}
            tick={TICK_STYLE as any}
            unit={s.unit ? ` ${s.unit}` : ''}
            width={60}
            domain={getYAxisDomain(s.id)}
          />
        ))}
        <Tooltip content={(p: any) => <ChartTooltip {...p} />} />
        {/* Synchronized hover line and markers from external components (e.g. Skew-T chart) */}
        {!isInternalHover && hoverTimestamp != null && hoverTimestamp >= xDomain[0] && hoverTimestamp <= xDomain[1] && (
          <ReferenceLine
            x={hoverTimestamp}
            yAxisId={seriesInfo[0]?.id}
            stroke="#64748b"
            strokeDasharray="3 3"
            strokeWidth={1.5}
            label={{
              value: formatTime(hoverTimestamp),
              position: 'insideTopRight',
              fill: '#475569',
              fontSize: 10,
              fontWeight: 600,
            }}
          />
        )}
        {!isInternalHover && hoveredRow && seriesInfo.map((s) => {
          const val = hoveredRow[s.id];
          if (val === null || val === undefined) return null;
          return (
            <ReferenceDot
              key={`dot-${s.id}`}
              yAxisId={s.id}
              x={hoveredRow.time}
              y={val}
              r={4}
              fill={s.color}
              stroke="#ffffff"
              strokeWidth={2}
            />
          );
        })}
        {seriesInfo.map((s) => (
          <Line
            key={s.id}
            yAxisId={s.id}
            type="monotone"
            dataKey={s.id}
            name={s.name}
            stroke={s.color}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
            connectNulls={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const FlightDiagrams: React.FC<FlightDiagramsProps> = ({
  flightId,
  coordinates = [],
  dateStart,
  dateEnd,
  hoverTimestamp,
  onHoverTimestamp,
  onClickTimestamp,
}) => {
  const [sensorData, setSensorData] = useState<Record<string, Array<{ time: string; value: number }>>>({});
  const [loadingSensor, setLoadingSensor] = useState(true);
  const [activeTabs, setActiveTabs] = useState<Set<string>>(new Set([GPS_ALTITUDE_KEY]));

  const toggleTab = (key: string) => {
    setActiveTabs(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size > 1) next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Fetch sensor readings once
  useEffect(() => {
    const load = async () => {
      setLoadingSensor(true);
      try {
        const res = await apiRequest(`/sensor-data/${flightId}`);
        if (res.ok) {
          const data = await res.json();
          const grouped: Record<string, Array<{ time: string; value: number }>> = {};
          for (const m of (data.measurements ?? [])) {
            (grouped[m.channel] ??= []).push({ time: m.timestamp, value: m.value });
          }
          setSensorData(grouped);
        }
      } catch {}
      finally { setLoadingSensor(false); }
    };
    load();
  }, [flightId]);

  // Build ordered tab list: GPS altitude first, then sensor channels sorted
  const tabs = useMemo(() => {
    const list: Array<{ key: string; label: string }> = [];
    if (coordinates && coordinates.length > 0) {
      list.push({ key: GPS_ALTITUDE_KEY, label: 'Höhe (GNSS)' });
    }
    for (const ch of Object.keys(sensorData).sort()) {
      list.push({ key: ch, label: getLabel(ch) });
    }
    return list;
  }, [coordinates, sensorData]);

  // Keep active tabs valid after sensor data loads
  useEffect(() => {
    if (tabs.length > 0) {
      setActiveTabs(prev => {
        const next = new Set(prev);
        next.forEach(key => {
          if (!tabs.find(t => t.key === key)) next.delete(key);
        });
        if (next.size === 0) next.add(tabs[0].key);
        return next;
      });
    }
  }, [tabs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute shared X-axis domain
  const sharedDomain = useMemo<[number, number]>(() => {
    const start = new Date(dateStart).getTime();
    let end = start;
    if (dateEnd) {
      end = new Date(dateEnd).getTime();
    } else {
      let maxTime = start;
      if (coordinates && coordinates.length > 0) {
        maxTime = Math.max(maxTime, new Date(coordinates[coordinates.length - 1].timestamp).getTime());
      }
      activeTabs.forEach(tab => {
        const readings = sensorData[tab];
        if (readings && readings.length > 0) {
          maxTime = Math.max(maxTime, new Date(readings[readings.length - 1].time).getTime());
        }
      });
      end = maxTime;
    }
    return [start, end];
  }, [dateStart, dateEnd, coordinates, activeTabs, sensorData]);

  // Resolve chart series
  const chart = useMemo(() => {
    const seriesInfo = [];
    if (activeTabs.has(GPS_ALTITUDE_KEY)) {
      seriesInfo.push({
        id: GPS_ALTITUDE_KEY,
        name: 'Höhe',
        unit: 'm',
        color: getColor(GPS_ALTITUDE_KEY)
      });
    }
    for (const tab of tabs) {
      if (tab.key !== GPS_ALTITUDE_KEY && activeTabs.has(tab.key)) {
        seriesInfo.push({
          id: tab.key,
          name: getLabel(tab.key),
          unit: getUnit(tab.key),
          color: getColor(tab.key)
        });
      }
    }
    
    const unifiedData = buildUnifiedData(activeTabs, dateStart, dateEnd, coordinates, sensorData);
    
    return (
      <MultiSeriesChart
        data={unifiedData}
        seriesInfo={seriesInfo}
        xDomain={sharedDomain}
        hoverTimestamp={hoverTimestamp}
        onHoverTimestamp={onHoverTimestamp}
        onClickTimestamp={onClickTimestamp}
      />
    );
  }, [activeTabs, dateStart, dateEnd, coordinates, sensorData, sharedDomain, tabs, hoverTimestamp, onHoverTimestamp, onClickTimestamp]);

  return (
    <div>
      {/* Tab pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px', alignItems: 'center' }}>
        {tabs.map(t => {
          const isActive = activeTabs.has(t.key);
          const activeColor = getColor(t.key);
          return (
            <button
              key={t.key}
              onClick={() => toggleTab(t.key)}
              style={{
                padding: '3px 14px',
                borderRadius: '20px',
                border: `1.5px solid ${isActive ? activeColor : '#dde1ea'}`,
                background: isActive ? activeColor : 'white',
                color: isActive ? 'white' : '#687088',
                fontSize: '12px',
                fontWeight: isActive ? 600 : 400,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.15s',
              }}
            >
              {t.label}
            </button>
          );
        })}
        {loadingSensor && (
          <span style={{ fontSize: '11px', color: '#c5cdd8', marginLeft: '2px' }}>
            Sensordaten werden geladen…
          </span>
        )}
      </div>

      {/* Chart area */}
      {tabs.length === 0 && !loadingSensor
        ? <NoData message="Keine Diagrammdaten verfügbar" />
        : chart}
    </div>
  );
};

export default React.memo(FlightDiagrams);
